import { useQuery } from '@apollo/client';
import { Badge, Button, Drawer, Result, Select, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download-solid.svg';
import { ReactComponent as ViewIcon } from '../../assets/svg/eye-solid.svg';
import { DATE_FORMATS, ORDER_STATUS, SORT } from '../../common/constants';
import { formatDate } from '../../common/utils';
import ColumnTitleWithInfo from '../ColumnTitleWithInfo';
import CommonTable from '../CommonTable';
import RenderCopyIcon from '../RenderCopyIcon';
import SearchComponent from '../SearchComponent';
import {
    INVENTORY_UNITS_DEFAULT_FILTERS,
    INVENTORY_UNITS_DEFAULT_PAGINATION,
    INVENTORY_UNITS_DEFAULT_SORT,
    INVENTORY_UNIT_QR_ACTIVE_OPTIONS,
    INVENTORY_UNIT_QR_IMAGE_STATUS,
    INVENTORY_UNIT_SORT_FIELDS,
    INVENTORY_UNIT_STATUS,
    TOOLTIP_Z_INDEX,
    UNIT_RETURN_ORDER_STATUS,
    humanizeEnum,
    toSelectOptions,
} from './constants';
import {
  getInventoryUnitApis,
  hasLibraryScope,
  withLibraryScope,
} from './apiConfig';
import DownloadQrCodesButton from './DownloadQrCodesButton';
import TransferUnitsButton from './TransferUnitsButton';
import InventoryUnitContentsModal from './InventoryUnitContentsModal';
import InventoryUnitLogsTable from './InventoryUnitLogsTable';
import RefreshButton from './RefreshButton';
import './inventoryUnits.less';
import { downloadFileFromUrl } from './utils';
import {
    EllipsisText,
    QrActiveTag,
    QrImageStatusTag,
    UnitStatusTag,
} from './InventoryUnitTags';

const STATUS_OPTIONS = toSelectOptions(INVENTORY_UNIT_STATUS);
const QR_IMAGE_STATUS_OPTIONS = toSelectOptions(INVENTORY_UNIT_QR_IMAGE_STATUS);
const SORTABLE_FIELDS = Object.values(INVENTORY_UNIT_SORT_FIELDS);

const getSortOrder = (sort, field) => {
  if (sort?.field !== field) return null;
  return sort?.order === SORT?.DESC ? 'descend' : 'ascend';
};

const buildFilter = ({ isLibrary, libraryId, toyId, filters, pagination }) => {
  const filter = {
    ...withLibraryScope(isLibrary, libraryId),
    toyId,
    skip: (pagination?.current - 1) * pagination?.pageSize,
    limit: pagination?.pageSize,
  };
  if (filters?.search) filter.search = filters?.search;
  if (filters?.status) filter.status = filters?.status;
  if (filters?.qrImageStatus) filter.qrImageStatus = filters?.qrImageStatus;
  if (typeof filters?.isQrActive === 'boolean') {
    filter.isQrActive = filters?.isQrActive;
  }
  return filter;
};

const InventoryUnitsContent = ({
  isLibrary,
  libraryId,
  toyId,
  isDesktop,
  onUnitsChanged,
}) => {
  const { units: unitsApi } = getInventoryUnitApis(isLibrary);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(INVENTORY_UNITS_DEFAULT_FILTERS);
  const [sort, setSort] = useState(INVENTORY_UNITS_DEFAULT_SORT);
  const [pagination, setPagination] = useState(
    INVENTORY_UNITS_DEFAULT_PAGINATION,
  );
  const [contentsUnit, setContentsUnit] = useState(null);
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);
  const [selectedUnitsById, setSelectedUnitsById] = useState({});

  const clearSelection = () => {
    setSelectedUnitIds([]);
    setSelectedUnitsById({});
  };

  const { data, previousData, loading, error, refetch } = useQuery(
    unitsApi?.document,
    {
      variables: {
        filter: buildFilter({
          isLibrary,
          libraryId,
          toyId,
          filters,
          pagination,
        }),
        sort,
      },
      skip: !hasLibraryScope(isLibrary, libraryId) || !toyId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError() {},
    },
  );

  const response = (data || previousData)?.[unitsApi?.key];
  const units = response?.data || [];
  const totalCount = response?.count || 0;

  const hasActiveFilters = useMemo(
    () =>
      Boolean(filters?.search) ||
      Boolean(filters?.status) ||
      Boolean(filters?.qrImageStatus) ||
      typeof filters?.isQrActive === 'boolean',
    [filters],
  );

  const updateFilters = (nextFilters) => {
    setFilters((prev) => ({ ...prev, ...nextFilters }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters(INVENTORY_UNITS_DEFAULT_FILTERS);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (tablePagination, _tableFilter, sorter) => {
    const isPageSizeChanged =
      tablePagination?.pageSize !== pagination?.pageSize;
    setPagination({
      current: isPageSizeChanged ? 1 : tablePagination?.current,
      pageSize: tablePagination?.pageSize,
    });
    if (sorter?.order && SORTABLE_FIELDS?.includes(sorter?.field)) {
      setSort({
        field: sorter?.field,
        order: sorter?.order === 'descend' ? SORT?.DESC : SORT?.ASC,
      });
    } else {
      setSort(INVENTORY_UNITS_DEFAULT_SORT);
    }
  };

  const columns = [
    {
      title: 'UNIT CODE',
      dataIndex: INVENTORY_UNIT_SORT_FIELDS?.UNIT_NUMBER,
      key: INVENTORY_UNIT_SORT_FIELDS?.UNIT_NUMBER,
      width: 200,
      sorter: true,
      fixed: isDesktop ? 'left' : false,
      sortOrder: getSortOrder(sort, INVENTORY_UNIT_SORT_FIELDS?.UNIT_NUMBER),
      render: (_, record) => (
        <>
          <div className="d-flex align-center">
            <RenderCopyIcon
              copyText={record?.unitCode}
              toolTipText="Copy Unit Code"
              zIndex={TOOLTIP_Z_INDEX}
            />
            <EllipsisText value={record?.unitCode} />
          </div>
          {record?.unitNumber !== null && record?.unitNumber !== undefined && (
            <div className="font-size-12">(Unit #{record?.unitNumber})</div>
          )}
        </>
      ),
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="UNIT STATUS"
          info="Last QR scanned status"
          zIndex={TOOLTIP_Z_INDEX}
        />
      ),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status) => <UnitStatusTag status={status} />,
    },
    {
      title: 'MAPPED ORDER NUMBER',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 220,
      render: (_, record) => {
        const assignment = record?.currentAssignment;
        const orderNumber = assignment?.isReturn
          ? assignment?.returnOrderNumber
          : assignment?.orderNumber;
        if (!orderNumber) return '-';
        return (
          <div className="d-flex align-center">
            <RenderCopyIcon
              copyText={orderNumber}
              toolTipText="Copy Order Number"
              zIndex={TOOLTIP_Z_INDEX}
            />
            <span>{orderNumber}</span>
          </div>
        );
      },
    },
    {
      title: 'MAPPED ORDER STATUS',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 220,
      render: (_, record) => {
        const assignment = record?.currentAssignment;
        const isReturn = Boolean(assignment?.isReturn);
        const orderStatus = isReturn
          ? assignment?.returnOrderStatus
          : assignment?.orderStatus;
        if (!orderStatus) return '-';
        const config = isReturn
          ? UNIT_RETURN_ORDER_STATUS?.[orderStatus]
          : ORDER_STATUS?.[orderStatus];
        return (
          <Badge
            color={config?.color}
            text={config?.text || humanizeEnum(orderStatus)}
          />
        );
      },
    },
    {
      title: 'SHELF NUMBER',
      dataIndex: 'shelfLocation',
      key: 'shelfLocation',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const location = [record?.rack?.name, record?.shelfLocation]
          ?.filter(Boolean)
          ?.join(' / ');
        return location || '-';
      },
    },
    {
      title: 'CONTENTS',
      dataIndex: 'totalPiecesPresent',
      key: 'totalPiecesPresent',
      width: 160,
      render: (_, record) => {
        const hasPieces =
          record?.totalPiecesPresent !== null &&
          record?.totalPiecesPresent !== undefined;
        if (!hasPieces) return '-';
        return <span>{`${record?.totalPiecesPresent} pcs`}</span>;
      },
    },
    {
      title: 'QR CODE',
      dataIndex: 'qrCode',
      key: 'qrCode',
      width: 200,
      render: (qrCode) =>
        qrCode ? (
          <div className="d-flex align-center">
            <RenderCopyIcon
              copyText={qrCode}
              toolTipText="Copy QR Code"
              zIndex={TOOLTIP_Z_INDEX}
            />
            <span>{qrCode}</span>
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'QR STATUS',
      dataIndex: 'isQrActive',
      key: 'isQrActive',
      width: 110,
      render: (isQrActive) => <QrActiveTag isActive={isQrActive} />,
    },
    {
      title: 'QR IMAGE STATUS',
      dataIndex: 'qrImageStatus',
      key: 'qrImageStatus',
      width: 160,
      render: (_, record) => (
        <QrImageStatusTag
          status={record?.qrImageStatus}
          error={record?.qrImageError}
        />
      ),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 190,
      render: (updatedAt) =>
        updatedAt ? formatDate(updatedAt, DATE_FORMATS?.WITH_TIME) : '-',
    },
    {
      title: 'CURRENT CONTENT & CHECKLIST',
      dataIndex: 'currentContents',
      key: 'currentContents',
      width: 240,
      render: (_, record) => {
        const hasContents = record?.currentContents?.length > 0;
        const hasChecklist = record?.currentChecklist?.length > 0;
        if (!hasContents && !hasChecklist) return '-';
        return (
          <Tooltip title="View current content and checklist" placement="top">
            <ViewIcon
              className="pointer svg-icon"
              onClick={() =>
                setContentsUnit({
                  unit: record,
                  mode: hasContents ? 'contents' : 'checklist',
                })
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 90,
      fixed: isDesktop ? 'right' : false,
      render: (_, record) => (
        <div className="d-flex align-center">
          <Tooltip
            title={
              record?.qrImageUrl ? 'Download QR' : 'QR image not available'
            }
            placement="top"
          >
            <DownloadIcon
              className={
                record?.qrImageUrl ? 'pointer svg-icon' : 'svg-icon-grey'
              }
              onClick={() =>
                record?.qrImageUrl &&
                downloadFileFromUrl(
                  record?.qrImageUrl,
                  `${record?.unitCode || record?.qrCode || record?.id}.png`,
                )
              }
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="inventory-units-drawer-content">
      <div className="inventory-units-filters">
        <SearchComponent
          className="inventory-units-search"
          name="by unit code / QR code"
          query={searchQuery}
          setQuery={setSearchQuery}
          getData={(value) => updateFilters({ search: value?.trim() })}
        />
        <Select
          allowClear
          className="inventory-units-filter-select"
          placeholder="Status"
          options={STATUS_OPTIONS}
          value={filters?.status}
          onChange={(status) => updateFilters({ status })}
        />
        <Select
          allowClear
          className="inventory-units-filter-select"
          placeholder="QR Status"
          options={INVENTORY_UNIT_QR_ACTIVE_OPTIONS}
          value={filters?.isQrActive}
          onChange={(isQrActive) => updateFilters({ isQrActive })}
        />
        <Select
          allowClear
          className="inventory-units-filter-select"
          placeholder="QR Image Status"
          options={QR_IMAGE_STATUS_OPTIONS}
          value={filters?.qrImageStatus}
          onChange={(qrImageStatus) => updateFilters({ qrImageStatus })}
        />
        {hasActiveFilters && (
          <Button type="link" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        )}
        <DownloadQrCodesButton
          type="primary"
          isLibrary={isLibrary}
          libraryId={libraryId}
          selectedUnitIds={selectedUnitIds}
          onSuccess={() => clearSelection()}
        />
        {!isLibrary && (
          <TransferUnitsButton
            units={selectedUnitIds?.map((id) => selectedUnitsById?.[id])}
            onTransferred={() => {
              clearSelection();
              refetch();
              if (onUnitsChanged) onUnitsChanged();
            }}
          />
        )}
        <div className="inventory-units-count">
          <span>Total Units {totalCount}</span>
          <RefreshButton
            title="Refresh units"
            loading={loading}
            onRefresh={refetch}
          />
        </div>
      </div>

      {error && !loading ? (
        <Result
          status="warning"
          title="Unable to load inventory units"
          subTitle={error?.message}
          extra={
            <Button
              type="primary"
              className="common-button"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          }
        />
      ) : (
        <CommonTable
          columns={columns}
          data={units}
          loadingData={loading}
          onChange={handleTableChange}
          paginationConfig={{
            current: pagination?.current,
            pageSize: pagination?.pageSize,
            total: totalCount,
          }}
          setHeight={isDesktop ? 280 : 360}
          rowKey={(record) => record?.id}
          rowSelection={{
            fixed: true,
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedUnitIds,
            onChange: (keys, rows) => {
              setSelectedUnitIds(keys);
              setSelectedUnitsById((prev) => {
                const next = {};
                rows?.filter(Boolean)?.forEach((row) => {
                  next[row?.id] = row;
                });
                keys?.forEach((key) => {
                  if (!next?.[key] && prev?.[key]) next[key] = prev?.[key];
                });
                return next;
              });
            },
          }}
          expandable={{
            expandedRowRender: (record) => (
              <InventoryUnitLogsTable
                isLibrary={isLibrary}
                unitId={record?.id}
                libraryId={libraryId}
              />
            ),
            rowExpandable: (record) => Boolean(record?.id),
          }}
        />
      )}

      <InventoryUnitContentsModal
        open={Boolean(contentsUnit)}
        unit={contentsUnit?.unit}
        mode={contentsUnit?.mode}
        isDesktop={isDesktop}
        onClose={() => setContentsUnit(null)}
      />
    </div>
  );
};

const InventoryUnitsDrawer = ({
  open,
  onClose,
  libraryId,
  inventory,
  isDesktop,
  isLibrary = false,
  onUnitsChanged,
}) => {
  const toyName = inventory?.toy?.name;
  const toyNumber = inventory?.toy?.toyNumber;

  return (
    <Drawer
      title={
        toyName
          ? `Inventory Units - ${toyName}${toyNumber ? ` (${toyNumber})` : ''}`
          : 'Inventory Units'
      }
      placement="right"
      open={open}
      onClose={onClose}
      width={isDesktop ? '85%' : '100%'}
      className="custom-drawer inventory-units-drawer"
      destroyOnClose
    >
      {hasLibraryScope(isLibrary, libraryId) && inventory?.toy?.id && (
        <InventoryUnitsContent
          key={inventory?.id}
          isLibrary={isLibrary}
          libraryId={libraryId}
          toyId={inventory?.toy?.id}
          isDesktop={isDesktop}
          onUnitsChanged={onUnitsChanged}
        />
      )}
    </Drawer>
  );
};

export default InventoryUnitsDrawer;
